import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { db } from './firebase';
import { collection, onSnapshot, query, orderBy } from './firestore-compat';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';

// --- FUNÇÕES AUXILIARES ---
const getWeekNumber = (d) => {
  if (!d || isNaN(d)) return null;
  d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  var yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  var weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return weekNo;
};

const diffInDays = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  if (!d1 || !d2 || isNaN(d1) || isNaN(d2)) return 0;
  const diffTime = Math.abs(d2 - d1);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const formatDateInput = (date) => {
  if (!date || isNaN(date)) return '';
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function Analysis() {
  const [allTasks, setAllTasks] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);

  // --- NOVOS FILTROS DE PERÍODO ---
  const [filterStartDate, setFilterStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [filterEndDate, setFilterEndDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
  const [filterResponsible, setFilterResponsible] = useState('todos');

  useEffect(() => {
    const unsubEtapas = onSnapshot(collection(db, 'etapas'), (snapshot) => {
      let tasks = [];
      snapshot.forEach(doc => {
        const etapaData = doc.data();
        if (etapaData.tarefas && Array.isArray(etapaData.tarefas)) {
          tasks = [...tasks, ...etapaData.tarefas.filter(t => t)];
        }
      });
      setAllTasks(tasks);
    });

    const unsubResponsaveis = onSnapshot(query(collection(db, 'responsaveis'), orderBy('nome')), (snapshot) => {
      setResponsaveis(snapshot.docs.map(doc => doc.data().nome));
    });

    return () => {
      unsubEtapas();
      unsubResponsaveis();
    };
  }, []);

  const {
    backlogAtrasadoTotal,
    totalTarefasPeriodo,
    concluidasNoPeriodo,
    percentualConcluidas,
    tempoMedioExecucao,
    prioridadeData,
    responsavelData,
    throughputMensalData,
  } = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const backlogAtrasadoTotal = allTasks.filter(task => {
      if (task.status === 'Feito' || !task.dataFim) return false;
      const dataFimObj = new Date(task.dataFim + 'T00:00:00');
      return dataFimObj < hoje;
    }).length;

    // Filtra pelo responsável selecionado
    const tasksOfResponsible = allTasks.filter(task => filterResponsible === 'todos' || task.responsavel === filterResponsible);

    // 1. Quantas tarefas total haviam no periodo (Previstas para terminar neste período)
    const tarefasNoPeriodoLista = tasksOfResponsible.filter(task => {
      if (!task.dataFim) return false;
      const dataFimObj = new Date(task.dataFim + "T00:00:00");
      if (isNaN(dataFimObj)) return false;
      return dataFimObj >= filterStartDate && dataFimObj <= filterEndDate;
    });
    const totalTarefasPeriodo = tarefasNoPeriodoLista.length;

    // 2. Quantas foram concluídas (dentre as que estavam previstas para o período)
    const tarefasConcluidasPeriodoLista = tarefasNoPeriodoLista.filter(task => task.status === 'Feito');
    const concluidasNoPeriodo = tarefasConcluidasPeriodoLista.length;

    // 3. Quantos % foram concluídas
    const percentualConcluidas = totalTarefasPeriodo > 0 
      ? Math.round((concluidasNoPeriodo / totalTarefasPeriodo) * 100) 
      : 0;

    // Para o Tempo Médio de Execução e Prioridades, vamos continuar usando as que foram de fato entregues/criadas no período para não perder o histórico
    const tasksCriadasNoPeriodo = tasksOfResponsible.filter(task => {
      const taskDate = task.dataInicio ? new Date(task.dataInicio + "T00:00:00") : null;
      if (!taskDate || isNaN(taskDate)) return false;
      return taskDate >= filterStartDate && taskDate <= filterEndDate;
    });

    const tasksConcluidasGlobalNoPeriodo = tasksOfResponsible.filter(task => {
      if (task.status !== 'Feito' || !task.dataConclusao) return false;
      const concDate = new Date(task.dataConclusao + "T00:00:00");
      if (isNaN(concDate)) return false;
      return concDate >= filterStartDate && concDate <= filterEndDate;
    });

    const tasksWithStartAndEnd = tasksConcluidasGlobalNoPeriodo.filter(t => t.dataInicio && t.dataConclusao);
    const totalExecutionDays = tasksWithStartAndEnd.reduce((sum, task) => sum + diffInDays(task.dataInicio, task.dataConclusao), 0);
    const tempoMedioExecucao = tasksWithStartAndEnd.length > 0 
      ? Math.round(totalExecutionDays / tasksWithStartAndEnd.length) 
      : 0;

    const prioridadeData = tasksCriadasNoPeriodo.reduce((acc, task) => {
      const prioridade = task.prioridade || 'Sem Prioridade';
      const existing = acc.find(item => item.name === prioridade);
      if (existing) {
        existing.value += 1;
      } else {
        acc.push({ name: prioridade, value: 1 });
      }
      return acc;
    }, []);

    const responsavelData = (filterResponsible === 'todos' ? responsaveis : [filterResponsible]).map(nome => {
      const tasksDoResponsavel = tasksConcluidasGlobalNoPeriodo.filter(t => t.responsavel === nome);
      const concluidasNoPrazo = tasksDoResponsavel.filter(t => t.dataFim && new Date(t.dataConclusao + "T00:00:00") <= new Date(t.dataFim + "T00:00:00")).length;
      const concluidasComAtraso = tasksDoResponsavel.length - concluidasNoPrazo;
      return { name: nome, 'Concluídas no Prazo': concluidasNoPrazo, 'Concluídas com Atraso': concluidasComAtraso };
    }).filter(d => d['Concluídas no Prazo'] > 0 || d['Concluídas com Atraso'] > 0);

    // O Throughput mensal deve olhar o histórico global (últimos 6 meses), ignorando o filtro de data atual
    const throughputMensalRaw = tasksOfResponsible
      .filter(t => t.status === 'Feito' && t.dataConclusao)
      .reduce((acc, task) => {
        const d = new Date(task.dataConclusao + "T00:00:00");
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});

    const throughputMensalData = Object.keys(throughputMensalRaw)
      .sort()
      .slice(-6)
      .map(key => {
        const [y, m] = key.split('-');
        const d = new Date(y, m - 1, 1);
        let label = d.toLocaleString('pt-BR', { month: 'short', year: '2-digit' });
        // Capitalizar a primeira letra do mês
        label = label.charAt(0).toUpperCase() + label.slice(1);
        return { name: label, 'Tarefas Concluídas': throughputMensalRaw[key] };
      });

    return { backlogAtrasadoTotal, totalTarefasPeriodo, concluidasNoPeriodo, percentualConcluidas, tempoMedioExecucao, prioridadeData, responsavelData, throughputMensalData };
  }, [allTasks, responsaveis, filterStartDate, filterEndDate, filterResponsible]);

  const prioridadeColors = { 'Baixa': '#17a2b8', 'Média': '#ffc107', 'Alta': '#fd7e14', 'Crítico': '#dc3545', 'Sem Prioridade': '#cccccc' };

  return (
    <div className="analysis-page-container">
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <h2 style={{ color: '#555', margin: 0 }}>Análises (Projetos)</h2>
      </div>
      <main className="analysis-main">
        <div className="analysis-controls">
          <div className="filter-group">
            <label>Período de Análise (De):</label>
            <input type="date" value={formatDateInput(filterStartDate)} onChange={(e) => setFilterStartDate(new Date(e.target.value + "T00:00:00"))} />
          </div>
          <div className="filter-group">
            <label>Período de Análise (Até):</label>
            <input type="date" value={formatDateInput(filterEndDate)} onChange={(e) => setFilterEndDate(new Date(e.target.value + "T00:00:00"))} />
          </div>
          <div className="filter-group">
            <label>Analisar Responsável:</label>
            <select value={filterResponsible} onChange={(e) => setFilterResponsible(e.target.value)}>
              <option value="todos">Todos</option>
              {responsaveis.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>

        <div className="kpi-container kpi-grid">
          <div className="kpi-card kpi-alerta"> <span className="kpi-value">{backlogAtrasadoTotal}</span> <span className="kpi-label">Backlog Atrasado (Total)</span> </div>
          <div className="kpi-card"> <span className="kpi-value">{totalTarefasPeriodo}</span> <span className="kpi-label">Total Previstas no Período</span> </div>
          <div className="kpi-card"> <span className="kpi-value kpi-feito">{concluidasNoPeriodo}</span> <span className="kpi-label">Concluídas do Período</span> </div>
          <div className="kpi-card"> <span className="kpi-value kpi-prazo">{percentualConcluidas}%</span> <span className="kpi-label">% Concluídas</span> </div>
          <div className="kpi-card"> <span className="kpi-value">{tempoMedioExecucao} dias</span> <span className="kpi-label">Tempo Médio de Execução</span> </div>
        </div>

        <div className="charts-container-grid">
          <div className="chart-wrapper">
            <h3>Desempenho de Entregas por Responsável</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={responsavelData} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={100} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Concluídas no Prazo" stackId="a" fill="#28a745" />
                <Bar dataKey="Concluídas com Atraso" stackId="a" fill="#dc3545" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-wrapper">
            <h3>Fluxo de Entregas Mensais</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={throughputMensalData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Tarefas Concluídas" fill="#007bff" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-wrapper">
            <h3>Distribuição por Prioridade</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={prioridadeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                  {prioridadeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={prioridadeColors[entry.name] || '#cccccc'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Analysis;